import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

export default function AdminManageParent() {
  const navigate = useNavigate();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Password reset state
  const [resettingId, setResettingId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadParents();
  }, []);

  const loadParents = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/admin/parents`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to load parents');
      const data = await res.json();
      setParents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (parentId, parentName) => {
    if (!window.confirm(`Are you sure you want to permanently delete the parent account for "${parentName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/admin/parents/${parentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to delete');
      
      setSuccessMsg(result.message);
      loadParents(); // Refresh list
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (parentId) => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/admin/parents/${parentId}/reset-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to reset password');
      
      setSuccessMsg(result.message);
      setResettingId(null);
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ Loading parents...</div>;

  return (
    <div style={{ maxWidth: 1000, margin: '40px auto', padding: 24, background: '#f8f9fa', borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>👨‍👩‍👧 Manage Parent Accounts</h2>
        <button 
          onClick={() => navigate('/admin/dashboard')} 
          style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15, border: '1px solid #f5c6cb' }}>
          ❌ {error}
        </div>
      )}
      {successMsg && (
        <div style={{ padding: 12, background: '#d4edda', color: '#155724', borderRadius: 6, marginBottom: 15, border: '1px solid #c3e6cb' }}>
          ✅ {successMsg}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #ddd', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
            <tr>
              <th style={{ padding: 12, textAlign: 'left' }}>Name</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Email</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Phone</th>
              <th style={{ padding: 12, textAlign: 'left' }}>Linked Students</th>
              <th style={{ padding: 12, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {parents.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: 30, textAlign: 'center', color: '#666' }}>
                  No parent accounts found. <button onClick={() => navigate('/admin/create-parent')} style={{ background: 'none', border: 'none', color: '#007BFF', cursor: 'pointer', textDecoration: 'underline' }}>Create one now</button>.
                </td>
              </tr>
            ) : (
              parents.map(parent => (
                <tr key={parent.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 12, fontWeight: 600 }}>{parent.name}</td>
                  <td style={{ padding: 12 }}>{parent.email}</td>
                  <td style={{ padding: 12, color: '#666' }}>{parent.phone || '—'}</td>
                  <td style={{ padding: 12 }}>
                    {parent.students.length > 0 ? (
                      <span style={{ background: '#e7f1ff', color: '#0056b3', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                        {parent.students.length} student(s)
                      </span>
                    ) : (
                      <span style={{ color: '#999', fontSize: 12 }}>None</span>
                    )}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      {resettingId === parent.id ? (
                        <div style={{ display: 'flex', gap: 5 }}>
                          <input 
                            type="password" 
                            placeholder="New password" 
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 12, width: 120 }}
                          />
                          <button 
                            onClick={() => handleResetPassword(parent.id)}
                            style={{ padding: '6px 10px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                          >
                            Save
                          </button>
                          <button 
                            onClick={() => { setResettingId(null); setNewPassword(''); setError(''); }}
                            style={{ padding: '6px 10px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <button 
                            onClick={() => setResettingId(parent.id)}
                            style={{ padding: '6px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                          >
                            🔑 Reset PW
                          </button>
                          <button 
                            onClick={() => handleDelete(parent.id, parent.name)}
                            style={{ padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                          >
                            🗑️ Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}